// src/pages/OelWanneBlock.jsx
// Oelauffangwanne pro Trafo - Maße, Volumen-Berechnung, SVG-Skizze.
// Nur sichtbar wenn Trafo Oel-Trafo ist.
//
// Wichtig: 1 Liter Trafooel ~ 0,895 kg, also Volumen-in-Liter = Oelmasse_kg / 0,895.
// Aus dem Excel-Vordruck: Umrechnungsfaktor 1 Liter Trafooel = 1 Kilogramm × 1,141.

import { C, Field, NumberInput, Checkbox, Row } from './formui';
import { t } from '../lib/sprache';

// Reine Bewertungs-Logik – kann auch ohne UI verwendet werden (z.B. in der Wartung,
// um automatisch einen Mangel im Prüfpunkt 05.1.5 anzulegen).
// Rückgabe: null wenn Trafo trocken oder keine Wanne, sonst {status, titel, text, volumen_liter, oel_liter_im_trafo}
export function computeOelwannenBewertung(trafo) {
  if (!trafo) return null;
  const isOel = trafo.trafoart && trafo.trafoart !== 'giessharz';
  if (!isOel) return null;
  const vorhanden = !!trafo.oel_wanne_vorhanden;
  if (!vorhanden) return null;

  const L = Number(trafo.oel_wanne_laenge_m) || 0;
  const B = Number(trafo.oel_wanne_breite_m) || 0;
  const H_cm = Number(trafo.oel_wanne_hoehe_cm) || 0;
  const H_m = H_cm / 100;
  const volumen_m3 = L * B * H_m;
  const volumen_liter = volumen_m3 * 1000;

  const oelmasse_kg = Number(trafo.gesamt_oel_gewicht_kg ?? trafo.oel_gewicht_kg) || 0;
  const oel_liter_im_trafo = oelmasse_kg * 1.141;

  const massenKomplett = L > 0 && B > 0 && H_cm > 0;
  const oelmengeBekannt = oelmasse_kg > 0;

  if (!massenKomplett && !oelmengeBekannt) {
    return { status: 'unbekannt', titel: t('Bewertung noch nicht moeglich'),
      text: t('Wannenmasse (L, B, H) und Trafo-Oelmenge (oben am Trafo unter "Gesamt-Oelgewicht kg") eintragen.'),
      volumen_liter, oel_liter_im_trafo };
  }
  // titel/text sind nur Anzeige (EditableStammdaten liest nur status) - daher uebersetzt.
  if (!massenKomplett) {
    return { status: 'unbekannt', titel: t('Bewertung noch nicht moeglich'),
      text: t('Bitte alle drei Wannenmasse (Laenge, Breite, Hoehe) eintragen.'),
      volumen_liter, oel_liter_im_trafo };
  }
  if (!oelmengeBekannt) {
    return { status: 'unbekannt', titel: t('Bewertung noch nicht moeglich'),
      text: t('Bitte oben am Trafo das Gesamt-Oelgewicht (kg) eintragen, sonst kann nicht berechnet werden, ob die Wanne ausreicht.'),
      volumen_liter, oel_liter_im_trafo };
  }
  if (trafo.trafo_in_oel_wanne) {
    return { status: 'mit_vorbehalt', titel: t('Bewertung nicht eindeutig'),
      text: t('Der Trafo steht IN der Wanne und verdraengt einen Teil des Auffangvolumens. Die effektive Auffangmenge ist kleiner als die {volumen} L Bruttovolumen und kann ohne Trafo-Standflaeche nicht exakt berechnet werden.', { volumen: volumen_liter.toFixed(0) }),
      volumen_liter, oel_liter_im_trafo };
  }
  if (volumen_liter >= oel_liter_im_trafo) {
    const reserve_l = volumen_liter - oel_liter_im_trafo;
    const reserve_pct = (reserve_l / oel_liter_im_trafo) * 100;
    return { status: 'ok', titel: t('OK - Wanne faengt das gesamte Trafoeol auf'),
      text: t('Wanne {wanne} L  >=  Trafooel {oel} L. Sicherheitsreserve: {reserve} L (+{prozent} %).', { wanne: volumen_liter.toFixed(0), oel: oel_liter_im_trafo.toFixed(0), reserve: reserve_l.toFixed(0), prozent: reserve_pct.toFixed(0) }),
      volumen_liter, oel_liter_im_trafo };
  }
  const fehl_l = oel_liter_im_trafo - volumen_liter;
  const fehl_pct = (fehl_l / oel_liter_im_trafo) * 100;
  return { status: 'nicht_ok', titel: t('NICHT OK - Wanne zu klein'),
    text: t('Wanne {wanne} L  <  Trafooel {oel} L. Fehlende Auffangkapazität: {fehlt} L (-{prozent} %). Mängelmeldung erforderlich.', { wanne: volumen_liter.toFixed(0), oel: oel_liter_im_trafo.toFixed(0), fehlt: fehl_l.toFixed(0), prozent: fehl_pct.toFixed(0) }),
    volumen_liter, oel_liter_im_trafo };
}

export default function OelWanneBlock({ trafo, onChange }) {
  const isOel = trafo.trafoart && trafo.trafoart !== 'giessharz';
  if (!isOel) return null;

  const vorhanden = !!trafo.oel_wanne_vorhanden;
  const L = Number(trafo.oel_wanne_laenge_m) || 0;
  const B = Number(trafo.oel_wanne_breite_m) || 0;
  const H_cm = Number(trafo.oel_wanne_hoehe_cm) || 0;
  const H_m = H_cm / 100;
  const volumen_m3 = L * B * H_m;
  const volumen_liter = volumen_m3 * 1000;

  // Oel im Trafo (kg) - Faustformel: 1 L = 1 kg * 1.141 -> Liter = kg * 1.141
  // Datenbank-Spalte heisst gesamt_oel_gewicht_kg (Fallback auf alten Feldnamen)
  const oelmasse_kg = Number(trafo.gesamt_oel_gewicht_kg ?? trafo.oel_gewicht_kg) || 0;
  const oel_liter_im_trafo = oelmasse_kg * 1.141;

  // ─── Bewertung: Passt das Trafooel in die Wanne? ───────────
  // status: 'ok' | 'nicht_ok' | 'unbekannt' | 'mit_vorbehalt'
  const bewertung = computeOelwannenBewertung(trafo);

  const bewertungStyle = {
    ok:            { bg: C.greenBg,  fg: C.green,  icon: 'OK' },
    nicht_ok:      { bg: C.redBg,    fg: C.red,    icon: '!'  },
    mit_vorbehalt: { bg: C.orangeBg, fg: C.orange, icon: '?'  },
    unbekannt:     { bg: C.gray100,  fg: C.gray700,icon: 'i'  },
  };

  return (
    <div style={{ marginTop: 14, padding: 14, background: C.gray100, borderRadius: 8 }}>
      <Checkbox checked={vorhanden} onChange={v => onChange('oel_wanne_vorhanden', v)}
        label={t('Ölauffangwanne unter diesem Trafo vorhanden')}/>

      {vorhanden && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: C.gray500, fontStyle: 'italic', marginBottom: 10 }}>
            {t('Umrechnung: 1 Liter Trafoöl ≈ 1 kg × 1,141')}
          </div>

          <Row columns={3}>
            <Field label={t('Länge L [m]')}>
              <NumberInput value={trafo.oel_wanne_laenge_m} onChange={v => onChange('oel_wanne_laenge_m', v)} step={0.01}/>
            </Field>
            <Field label={t('Breite B [m]')}>
              <NumberInput value={trafo.oel_wanne_breite_m} onChange={v => onChange('oel_wanne_breite_m', v)} step={0.01}/>
            </Field>
            {/* Alle Masse in Meter (Anna-Lena 18.09.2026). Gespeichert wird die
                Hoehe weiter in cm (Spalte oel_wanne_hoehe_cm) - nur die Eingabe ist in m. */}
            <Field label={t('Höhe H [m]')}>
              <NumberInput value={trafo.oel_wanne_hoehe_cm != null && trafo.oel_wanne_hoehe_cm !== '' ? Math.round(Number(trafo.oel_wanne_hoehe_cm) * 10) / 1000 : null}
                onChange={v => onChange('oel_wanne_hoehe_cm', v == null ? null : Math.round(v * 1000) / 10)} step={0.01}/>
            </Field>
          </Row>

          {/* SVG-Skizze */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
            <svg width='200' height='150' viewBox='0 0 200 150' style={{ background: C.white, borderRadius: 6 }}>
              {/* Wannen-Boden mit Aussenkonturen */}
              <rect x='30' y='40' width='140' height='80' fill='none' stroke={C.navy} strokeWidth='2'/>
              {/* Innenbereich */}
              <rect x='34' y='44' width='132' height='72' fill={C.yellowBg} stroke='none'/>
              {/* Maßpfeile */}
              <line x1='30' y1='130' x2='170' y2='130' stroke={C.gray500} strokeWidth='1' markerEnd='url(#arrR)' markerStart='url(#arrL)'/>
              <text x='100' y='144' textAnchor='middle' fontSize='10' fill={C.gray700}>L = {L || '?'} m</text>
              <line x1='15' y1='40' x2='15' y2='120' stroke={C.gray500} strokeWidth='1' markerEnd='url(#arrD)' markerStart='url(#arrU)'/>
              <text x='10' y='84' textAnchor='middle' fontSize='10' fill={C.gray700} transform='rotate(-90 10 84)'>B = {B || '?'} m</text>
              <defs>
                <marker id='arrL' markerWidth='6' markerHeight='6' refX='3' refY='3' orient='auto'><path d='M6,0 L0,3 L6,6 z' fill={C.gray500}/></marker>
                <marker id='arrR' markerWidth='6' markerHeight='6' refX='3' refY='3' orient='auto'><path d='M0,0 L6,3 L0,6 z' fill={C.gray500}/></marker>
                <marker id='arrU' markerWidth='6' markerHeight='6' refX='3' refY='3' orient='auto'><path d='M0,6 L3,0 L6,6 z' fill={C.gray500}/></marker>
                <marker id='arrD' markerWidth='6' markerHeight='6' refX='3' refY='3' orient='auto'><path d='M0,0 L3,6 L6,0 z' fill={C.gray500}/></marker>
              </defs>
            </svg>

            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>{t('Berechnetes Volumen')}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginTop: 4 }}>
                {volumen_m3.toFixed(3)} m³ ≈ {volumen_liter.toFixed(0)} L
              </div>
              {oelmasse_kg > 0 && (
                <div style={{ fontSize: 14, color: C.gray700, marginTop: 4 }}>
                  {t('Trafo-Ölmenge: {kg} kg ≈ {liter} L', { kg: oelmasse_kg.toFixed(0), liter: oel_liter_im_trafo.toFixed(0) })}
                </div>
              )}
            </div>
          </div>

          {/* Trafo in der Wanne */}
          <Checkbox
            checked={!!trafo.trafo_in_oel_wanne}
            onChange={v => onChange('trafo_in_oel_wanne', v)}
            label={t('Trafo steht IN der Wanne (verdrängt Volumen)')}/>

          {/* === BEWERTUNG === */}
          {bewertung && (() => {
            const s = bewertungStyle[bewertung.status];
            return (
              <div style={{
                marginTop: 12,
                padding: 12,
                background: s.bg,
                borderRadius: 8,
                borderLeft: '4px solid ' + s.fg,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}>
                <div style={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: s.fg,
                  color: C.white,
                  fontWeight: 800,
                  fontSize: 15,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>{s.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 13,
                    color: s.fg,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontWeight: 700,
                    marginBottom: 2,
                  }}>{t('Bewertung Auffangwanne')}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.fg, marginBottom: 4 }}>
                    {bewertung.titel}
                  </div>
                  <div style={{ fontSize: 14, color: C.gray700, lineHeight: 1.4 }}>
                    {bewertung.text}
                  </div>
                </div>
              </div>
            );
          })()}

          <Field label={t('Notiz zur Ölwanne (optional)')}>
            <input type='text' value={trafo.oel_wanne_notiz || ''}
              onChange={e => onChange('oel_wanne_notiz', e.target.value)}
              placeholder={t('z.B. Material, Zustand, Beschichtung')}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1.5px solid ' + C.gray200, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: C.white, fontFamily: 'inherit' }}/>
          </Field>
        </div>
      )}
    </div>
  );
}
